import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { Timeline, notification, Modal, Table, Empty, Alert } from "antd";
import moment from "moment";

const BDFLogsContainer = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { id: bdfId } = useParams();
  const [bdfLogs, setBdfLogs] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState("");

  const getBdfLogsById = async () => {
    try {
      let response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getBdfLogsById?bdfId=${bdfId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const responseData = await response.json();
      if (response.ok) {
        setBdfLogs(responseData.data.logs || []); // Store the fetched data in state
      } else {
        notification.error({
          message: "History not found!",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Occurred while fetching sales orders",
      });
    }
  };

  useEffect(() => {
    if (bdfId) {
      getBdfLogsById(); // Fetch data when component mounts
    }
  }, [bdfId]);

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setModalContent("");
  };

  console.log("bdfLogs: ", bdfLogs);

  const showModal = (log, nextLog) => {
    const logDetails = [
      {
        label: "GST Number",
        value: log?.parentCompanyId?.gstNumber,
        key: "parentCompanyId.gstNumber",
      },
      {
        label: "Sales Person Name",
        value: log?.salesPerson,
        key: "salesPerson",
      },
      {
        label: "Salesman Base Location",
        value: log?.salesPersonBaseLocation,
        key: "salesPersonBaseLocation",
      },
      {
        label: "Media Planner Name",
        value: log?.mediaPlannerName,
        key: "mediaPlannerName",
      },
      {
        label: "Client Category",
        value: log?.clientCategory,
        key: "clientCategory",
      },
      { label: "Lead From", value: log?.leadFrom, key: "leadFrom" },
      { label: "RO", value: log?.ro, key: "ro" },
      {
        label: "Parent Company Name",
        value: log?.parentCompanyName,
        key: "parentCompanyName",
      },
      {
        label: "Brand Name",
        value: log?.brandId?.brandName,
        key: "brandId.brandName",
      },
      {
        label: "Billing Party Name",
        value: log?.billingPartyName,
        key: "billingPartyName",
      },
      { label: "Campaign Name", value: log?.campaignName, key: "campaignName" },
      {
        label: "Billing Address",
        value: log?.billingAddress,
        key: "billingAddress",
      },
      { label: "Brand Segment", value: log?.brandSegment, key: "brandSegment" },
      {
        label: "Contact Person",
        value: log?.contactPerson,
        key: "contactPerson",
      },
      {
        label: "Contact Number",
        value: log?.contactNumber,
        key: "contactNumber",
      },
      { label: "Email", value: log?.emailId, key: "emailId" },
      { label: "Credit Period", value: log?.creditPeriod, key: "creditPeriod" },
      { label: "Remark", value: log?.remark, key: "remark" },
    ];

    // Function to compare log values with the next log
    const isChanged = (key) => {
      if (!nextLog) return false;
      const currentValue = key
        .split(".")
        .reduce((acc, part) => acc?.[part], log);
      const nextValue = nextLog
        ? key.split(".").reduce((acc, part) => acc?.[part], nextLog)
        : null;
      return currentValue !== nextValue;
    };

    // Function to compare inventory values with the next log
    const isInventoryChanged = (key, index) => {
      if (!nextLog) return false;
      let currentValue = key
        .split(".")
        .reduce((acc, part) => acc?.[part], log?.inventories?.[index]);
      let nextValue = key
        .split(".")
        .reduce((acc, part) => acc?.[part], nextLog?.inventories?.[index]);

      // Handle bookingPeriod comparison
      if (key === "bookingPeriod") {
        currentValue = `${moment(log?.inventories?.[index]?.startDate).format(
          "DD/MM/YYYY"
        )} - ${moment(log?.inventories?.[index]?.endDate).format(
          "DD/MM/YYYY"
        )}`;
        nextValue = `${moment(nextLog?.inventories?.[index]?.startDate).format(
          "DD/MM/YYYY"
        )} - ${moment(nextLog?.inventories?.[index]?.endDate).format(
          "DD/MM/YYYY"
        )}`;
      }

      return currentValue !== nextValue;
    };

    // Function to compare suffix inventory values with the next log
    const isSuffixChanged = (key, index) => {
      if (!nextLog) return false;
      const currentValue = key
        .split(".")
        .reduce((acc, part) => acc?.[part], log?.suffixInventories?.[index]);
      const nextValue = key
        .split(".")
        .reduce(
          (acc, part) => acc?.[part],
          nextLog?.suffixInventories?.[index]
        );

      console.log("currentValue: ", currentValue, "nextValue: ", nextValue);
      return currentValue !== nextValue;
    };

    // Inventory Table Data
    const inventories = log?.inventories || [];
    const inventoryColumns = [
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("invId.locations.name", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "RPM",
        dataIndex: "rpm",
        key: "rpm",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("rpm", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Mounting Charge",
        dataIndex: "mountingCharge",
        key: "mountingCharge",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("mountingCharge", index);
          console.log("isChanged: ", isChanged);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Innovation Charge",
        dataIndex: "innovationCharge",
        key: "innovationCharge",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("innovationRate", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Printing Charge",
        dataIndex: "printingCharge",
        key: "printingCharge",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("printingCharge", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Special Rate Per Month",
        dataIndex: "specialRatePerMonth",
        key: "specialRatePerMonth",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("specialRatePerMonth", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: (
          <div>
            <div>Amount</div>
            <div>(In A/C)</div>
          </div>
        ),
        dataIndex: "specialRate",
        key: "specialRate",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("specialRate", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Booking Period",
        dataIndex: "bookingPeriod",
        key: "bookingPeriod",
        render: (text, record, index) => {
          const isChanged = isInventoryChanged("bookingPeriod", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
    ];
    const inventoryData = inventories.map((inv, index) => ({
      key: index,
      location: inv?.invId?.locations?.name || "N/A",
      rpm: inv?.rpm || "N/A",
      mountingCharge: inv?.mountingCharge || "N/A",
      innovationCharge: inv?.innovationRate || "N/A",
      printingCharge: inv?.printingCharge || "N/A",
      specialRatePerMonth: inv?.specialRatePerMonth || "N/A",
      specialRate: inv?.specialRate || "N/A",
      bookingPeriod: `${moment(inv?.startDate).format("DD/MM/YYYY")} - ${moment(
        inv?.endDate
      ).format("DD/MM/YYYY")}`,
    }));

    // Suffix Inventories Table Data
    const suffixInventories = log?.suffixInventories || [];
    const suffixColumns = [
      {
        title: "Code",
        dataIndex: "code",
        key: "code",
        render: (text, record, index) => {
          const isChanged = isSuffixChanged("suffixCode", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        render: (text, record, index) => {
          const isChanged = isSuffixChanged("specialRate", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Suffix Info",
        dataIndex: "suffixInfo",
        key: "suffixInfo",
        render: (text, record, index) => {
          const isChanged = isSuffixChanged("suffixInfo", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
      {
        title: "Vendor",
        dataIndex: "vendor",
        key: "vendor",
        render: (text, record, index) => {
          const isChanged = isSuffixChanged("vendor", index);
          return {
            children: text,
            props: {
              style: {
                backgroundColor: isChanged ? "#ffe58f" : "transparent",
                textAlign: "center",
              },
            },
          };
        },
      },
    ];
    const suffixData = suffixInventories.map((suffInv, index) => ({
      key: index,
      code: suffInv?.suffixCode || "N/A",
      price: suffInv?.specialRate || "N/A",
      suffixInfo: suffInv?.suffixInfo || "N/A",
      vendor: suffInv?.vendor || "N/A",
    }));

    const formattedContent = (
      <div>
        {log?.status !== "created" && (
          <Alert
            message={<span>Yellow color indicates the field was edited!</span>}
            type="warning"
            style={{ marginBottom: "1rem" }}
          />
        )}
        <h4>BDF Details</h4>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0",
            border: "1px solid #ccc",
          }}
        >
          {logDetails.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                width: "calc(33.33% - 0px)",
                padding: "0.5rem",
                borderRight: "1px solid #ccc",
                borderBottom:
                  index >= logDetails.length - 3 ? "none" : "1px solid #ccc",
                boxSizing: "border-box",
                backgroundColor: isChanged(item.key)
                  ? "#ffe58f"
                  : "transparent",
              }}
            >
              <strong>{item.label}:</strong> &nbsp;
              <span>{item.value || "N/A"}</span>
            </div>
          ))}
        </div>

        {inventories?.length > 0 && (
          <div style={{ marginTop: "2rem" }}>
            <h4>Inventories</h4>
            <Table
              columns={inventoryColumns}
              dataSource={inventoryData}
              pagination={false}
              bordered
              components={{
                header: {
                  cell: ({ children, ...restProps }) => (
                    <th
                      {...restProps}
                      style={{
                        backgroundColor: "#DAEFFE",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      {children}
                    </th>
                  ),
                },
                // body: {
                //   cell: ({ children, ...restProps }) => (
                //     <td
                //       {...restProps}
                //       style={{
                //         textAlign: "center",
                //         verticalAlign: "middle",
                //       }}
                //     >
                //       {children}
                //     </td>
                //   ),
                // },
              }}
            />
          </div>
        )}

        {suffixInventories?.length > 0 && (
          <div style={{ marginTop: "2rem" }}>
            <h4>Suffix Inventories</h4>
            <Table
              columns={suffixColumns}
              dataSource={suffixData}
              pagination={false}
              bordered
              components={{
                header: {
                  cell: ({ children, ...restProps }) => (
                    <th
                      {...restProps}
                      style={{
                        backgroundColor: "#DAEFFE",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      {children}
                    </th>
                  ),
                },
                // body: {
                //   cell: ({ children, ...restProps }) => (
                //     <td
                //       {...restProps}
                //       style={{
                //         textAlign: "center",
                //         verticalAlign: "middle",
                //       }}
                //     >
                //       {children}
                //     </td>
                //   ),
                // },
              }}
            />
          </div>
        )}
      </div>
    );

    setModalContent(formattedContent);
    setIsModalVisible(true);
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "0px 20px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginTop: "1rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "1rem 1rem",
            cursor: "pointer",
          }}
          onClick={() => {
            const searchParams = window.location.search.startsWith("?")
              ? window.location.search.substring(1) 
              : "";
          
            const url = searchParams ? `/clients-bookings?tab=1&${searchParams}` : `/clients-bookings?tab=1`;
        
            navigate(url);
          }}
        >
          <FaArrowLeft style={{ marginRight: "10px" }} />
        </div>

        <h3>BDF History</h3>
      </div>
      <div
        style={{
          marginTop: "20px",
          marginRight: "10rem",
        }}
      >
        <Timeline mode="left">
          {bdfLogs &&
            bdfLogs.length > 0 &&
            bdfLogs
              .slice()
              .reverse()
              .map((log, index) => (
                <Timeline.Item
                  key={index}
                  label={moment(log?.timestamp).format("DD-MM-YYYY hh:mm A")}
                  style={{ fontSize: "20px" }}
                >
                  <div style={{ display: "flex" }}>
                    <span>
                      {log?.status} by {log?.modifiedBy?.name}
                    </span>
                    <span
                      style={{
                        color: "#1677ff",
                        cursor: "pointer",
                        marginLeft: "2rem",
                      }}
                      onClick={() =>
                        showModal(log, bdfLogs[bdfLogs.length - 2 - index])
                      }
                      // showModal(log, bdfLogs[bdfLogs.length - 2 - index]);
                    >
                      View
                    </span>
                  </div>
                </Timeline.Item>
              ))}
        </Timeline>

        {bdfLogs === null && (
          <Empty
            description={
              <span
                style={{ fontSize: "24px", fontWeight: "bold", color: "grey" }}
              >
                No history available!
              </span>
            }
            style={{ padding: "50px" }}
          />
        )}
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        title={null}
        width={1500}
        centered
        styles={{
          body: {
            maxHeight: "800px",
            overflowY: "auto",
            padding: "20px",
          },
        }}
      >
        <div style={{ padding: "20px", fontSize: "16px" }}>{modalContent}</div>
      </Modal>
    </div>
  );
};

export default BDFLogsContainer;
